/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Grid view of all components with search and filtering
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - Browse all components in a grid
 * - Filter by category, status
 * - Search by keyword
 * - Navigate to: ComponentDetail, ComponentNew
 * </page-design>
 */

import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as ComponentListStore from '@/stores/ComponentListStore'
import { pageLinks } from '@/pageLinks'

const statusStyles: Record<string, string> = {
  published: 'bg-emerald-50 text-emerald-600',
  draft: 'bg-amber-50 text-amber-600',
  review: 'bg-sky-50 text-sky-600',
  archived: 'bg-gray-100 text-gray-500',
}
const statusLabels: Record<string, string> = {
  published: '已发布', draft: '草稿', review: '审核中', archived: '已归档',
}

/** 根据组件名称自动渲染迷你 UI 预览 */
function ComponentPreviewThumbnail({ name }: { name: string }) {
  const n = name.toLowerCase()

  if (n === 'button') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          <div className="px-4 py-1.5 bg-[#1D263A] text-white text-[10px] font-medium rounded-lg shadow-sm">主要按钮</div>
          <div className="px-4 py-1.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded-lg">次要按钮</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-4 py-1.5 border border-gray-200 text-gray-500 text-[10px] font-medium rounded-lg">描边</div>
          <div className="px-4 py-1.5 bg-red-500 text-white text-[10px] font-medium rounded-lg shadow-sm">危险</div>
        </div>
      </div>
    )
  }

  if (n === 'input') {
    return (
      <div className="w-40 flex flex-col gap-2">
        <div className="flex items-center bg-white rounded-lg border border-gray-200 px-3 py-1.5 shadow-sm">
          <Icon icon="lucide:search" className="text-gray-300 text-xs mr-1.5" />
          <span className="text-[10px] text-gray-300">请输入内容...</span>
        </div>
        <div className="flex items-center bg-white rounded-lg border border-[#1D263A]/30 px-3 py-1.5 ring-2 ring-[#1D263A]/5">
          <span className="text-[10px] text-gray-700">已输入文本</span>
          <div className="w-px h-3 bg-[#1D263A] ml-0.5 animate-pulse" />
        </div>
      </div>
    )
  }

  if (n === 'modal') {
    return (
      <div className="relative w-44">
        <div className="absolute inset-0 bg-black/5 rounded-lg" />
        <div className="relative bg-white rounded-xl shadow-lg mx-3 my-1 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-800">确认操作</span>
            <Icon icon="lucide:x" className="text-gray-300 text-xs" />
          </div>
          <div className="text-[9px] text-gray-400 mb-2.5">确定要执行此操作吗？</div>
          <div className="flex justify-end gap-1.5">
            <div className="px-2.5 py-1 bg-gray-100 text-gray-500 text-[9px] rounded-md">取消</div>
            <div className="px-2.5 py-1 bg-[#1D263A] text-white text-[9px] rounded-md">确定</div>
          </div>
        </div>
      </div>
    )
  }

  if (n === 'select') {
    return (
      <div className="w-36 flex flex-col">
        <div className="flex items-center justify-between bg-white rounded-lg border border-[#1D263A]/30 px-3 py-1.5 shadow-sm">
          <span className="text-[10px] text-gray-700">选项 A</span>
          <Icon icon="lucide:chevron-down" className="text-gray-400 text-xs" />
        </div>
        <div className="bg-white rounded-lg shadow-lg border border-gray-100 mt-0.5 py-0.5">
          <div className="px-3 py-1 text-[10px] text-[#1D263A] bg-[#1D263A]/5 font-medium">选项 A</div>
          <div className="px-3 py-1 text-[10px] text-gray-500">选项 B</div>
          <div className="px-3 py-1 text-[10px] text-gray-500">选项 C</div>
        </div>
      </div>
    )
  }

  if (n === 'avatar') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D263A] to-[#151D2E] text-white flex items-center justify-center text-[10px] font-bold shadow-sm">张</div>
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center text-[9px] font-bold">李</div>
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center text-[9px] font-bold">王</div>
        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[8px] text-gray-400">+3</div>
      </div>
    )
  }

  if (n === 'tabs') {
    return (
      <div className="w-44">
        <div className="flex bg-gray-100 rounded-lg p-0.5 mb-2">
          <div className="flex-1 text-center text-[9px] font-medium py-1 rounded-md bg-white text-[#1D263A] shadow-sm">概览</div>
          <div className="flex-1 text-center text-[9px] text-gray-400 py-1">详情</div>
          <div className="flex-1 text-center text-[9px] text-gray-400 py-1">设置</div>
        </div>
        <div className="bg-white/60 rounded-md h-6 flex items-center px-2">
          <div className="h-1.5 bg-gray-200 rounded-full w-3/4" />
        </div>
      </div>
    )
  }

  if (n === 'card') {
    return (
      <div className="w-36 bg-white rounded-xl shadow-md p-2.5 border border-gray-50">
        <div className="h-10 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg mb-2 flex items-center justify-center">
          <Icon icon="lucide:image" className="text-gray-200 text-lg" />
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full w-3/4 mb-1" />
        <div className="h-1.5 bg-gray-100 rounded-full w-1/2" />
      </div>
    )
  }

  if (n === 'badge') {
    return (
      <div className="flex flex-wrap items-center gap-2 justify-center">
        <span className="px-2 py-0.5 text-[9px] font-medium bg-emerald-50 text-emerald-600 rounded-full">已完成</span>
        <span className="px-2 py-0.5 text-[9px] font-medium bg-amber-50 text-amber-600 rounded-full">进行中</span>
        <span className="px-2 py-0.5 text-[9px] font-medium bg-sky-50 text-sky-600 rounded-full">待审核</span>
        <span className="px-2 py-0.5 text-[9px] font-medium bg-red-50 text-red-600 rounded-full flex items-center gap-0.5"><Icon icon="lucide:alert-circle" className="text-[9px]" />错误</span>
      </div>
    )
  }

  if (n === 'tooltip') {
    return (
      <div className="flex flex-col items-center">
        <div className="bg-gray-800 text-white text-[9px] px-2.5 py-1 rounded-md shadow-lg relative mb-1">
          提示信息
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-transparent border-t-gray-800" />
        </div>
        <div className="text-[10px] text-[#1D263A] border-b border-dashed border-[#1D263A]/30">悬停查看</div>
      </div>
    )
  }

  if (n === 'breadcrumb') {
    return (
      <div className="flex items-center gap-1 text-[10px]">
        <span className="text-[#1D263A]/50">首页</span>
        <Icon icon="lucide:chevron-right" className="text-gray-300 text-[9px]" />
        <span className="text-[#1D263A]/50">组件</span>
        <Icon icon="lucide:chevron-right" className="text-gray-300 text-[9px]" />
        <span className="text-[#1D263A] font-medium">Breadcrumb</span>
      </div>
    )
  }

  if (n === 'switch') {
    return (
      <div className="flex flex-col gap-2 items-center">
        <div className="flex items-center gap-2">
          <div className="w-8 h-[18px] bg-[#1D263A] rounded-full relative shadow-sm"><div className="absolute right-0.5 top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow" /></div>
          <span className="text-[10px] text-gray-500">开启</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-[18px] bg-gray-200 rounded-full relative"><div className="absolute left-0.5 top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow" /></div>
          <span className="text-[10px] text-gray-400">关闭</span>
        </div>
      </div>
    )
  }

  if (n === 'tag') {
    return (
      <div className="flex flex-wrap gap-1.5 justify-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-medium bg-[#1D263A]/5 text-[#1D263A] rounded-md">React <Icon icon="lucide:x" className="text-[8px] text-[#1D263A]/40" /></span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-medium bg-emerald-50 text-emerald-600 rounded-md">TypeScript</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-medium bg-sky-50 text-sky-600 rounded-md">UI</span>
      </div>
    )
  }

  // 默认：显示组件名缩写
  return (
    <div className="w-14 h-14 rounded-2xl bg-[#1D263A]/5 flex items-center justify-center text-[#1D263A]/40 text-lg font-bold">
      {name.slice(0, 2)}
    </div>
  )
}

function Toolbar() {
  const keyword = useAtomValue(ComponentListStore.filterKeywordAtom)
  const categoryId = useAtomValue(ComponentListStore.filterCategoryAtom)
  const status = useAtomValue(ComponentListStore.filterStatusAtom)
  const setKeyword = useSetAtom(ComponentListStore.filterKeywordAtom)
  const setCategory = useSetAtom(ComponentListStore.filterCategoryAtom)
  const setStatus = useSetAtom(ComponentListStore.filterStatusAtom)
  const { data } = useAtomValue(ComponentListStore.refreshAtom)

  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="relative flex-1 max-w-xs group">
        <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-[#1D263A] transition-colors" />
        <input type="text" placeholder="搜索组件..." value={keyword} onChange={e => setKeyword(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white rounded-full text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 focus:shadow-[0_4px_12px_rgb(29,38,58,0.08)] transition-all placeholder-gray-400" />
      </div>
      <select value={categoryId} onChange={e => setCategory(e.target.value)} className="px-4 py-2.5 bg-white rounded-xl text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 transition-all text-gray-700">
        <option value="">全部分类</option>
        {data?.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select value={status} onChange={e => setStatus(e.target.value)} className="px-4 py-2.5 bg-white rounded-xl text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 transition-all text-gray-700">
        <option value="">全部状态</option>
        <option value="draft">草稿</option>
        <option value="review">审核中</option>
        <option value="published">已发布</option>
        <option value="archived">已归档</option>
      </select>
    </div>
  )
}

function ComponentGrid() {
  const { data, isPending } = useAtomValue(ComponentListStore.refreshAtom)
  if (isPending) {
    return (
      <div className="grid grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] animate-pulse overflow-hidden">
            <div className="aspect-video bg-gray-100" />
            <div className="p-4"><div className="h-5 bg-gray-100 rounded w-2/3 mb-2" /><div className="h-4 bg-gray-100 rounded w-1/3" /></div>
          </div>
        ))}
      </div>
    )
  }
  if (!data || data.components.length === 0) {
    return <div className="text-center py-20 text-gray-400"><Icon icon="lucide:inbox" className="text-5xl mb-4 mx-auto" /><p>暂无组件</p></div>
  }
  return (
    <div className="grid grid-cols-4 gap-6">
      {data.components.map(comp => (
        <Link key={comp.id} to={pageLinks.ComponentDetail(comp.id)} className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 overflow-hidden group cursor-pointer">
          <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden">
            {comp.thumbnailUrl ? <img src={comp.thumbnailUrl} alt={comp.name} className="w-full h-full object-cover" /> : <ComponentPreviewThumbnail name={comp.name} />}
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold truncate">{comp.name}</h3>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ml-2 ${statusStyles[comp.status] || ''}`}>{statusLabels[comp.status] || comp.status}</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400 font-mono">{comp.currentVersion}</span>
              <span className="text-xs text-gray-400">{comp.updatedAt ? new Date(comp.updatedAt).toLocaleDateString('zh-CN') : ''}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}

export default function ComponentList() {
  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        <header className="flex items-center justify-between mb-6 px-2">
          <h1 className="text-2xl font-bold tracking-tight">组件库</h1>
          <Link to={pageLinks.ComponentNew()} className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] hover:shadow-[0_6px_16px_rgba(29,38,58,0.35)] transition-all flex items-center gap-2 text-sm"><Icon icon="lucide:plus" className="text-lg" />新建组件</Link>
        </header>
        <div className="px-2"><Toolbar /></div>
        <div className="flex-1 overflow-y-auto pb-8 px-2"><ComponentGrid /></div>
      </div>
    </div>
  )
}
